import {
  Box,
  Chip,
  Collapse,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FieldBox } from 'components/fieldBox';
import { formatTranslation } from 'intl';
import { FieldTranslationKeys } from 'intl/keys';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { ChangeEvent, ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { Color } from 'shared/utils/colors';

const QUICK_OPTIONS = [7, 14, 30];

export function FieldVisibilityBox({
  label,
  disabled,
  fieldName,
  isChecked,
  onChange,
  supportsDefaultDays,
  defaultDays,
  onDefaultDaysChange,
}: {
  label: string;
  disabled: boolean;
  fieldName: IssueFieldNames;
  isChecked: boolean;
  onChange: (event: any, value: boolean) => void;
  supportsDefaultDays?: boolean;
  defaultDays?: number;
  onDefaultDaysChange?: (fieldName: IssueFieldNames, value: number | undefined) => void;
}): ReactElement {
  const intl = useIntl();

  if (!supportsDefaultDays) {
    return (
      <FieldBox dataQa={`field-visibility-box-${fieldName}`}>
        <span style={{ flexGrow: 1 }}>{label}</span>
        <ForbiddenTooltip
          visibleWhen={disabled}
          reason={FieldTranslationKeys.visibilitySwitchDisabled}
        >
          <Switch
            id={fieldName}
            checked={isChecked}
            disabled={disabled}
            color='primary'
            onChange={onChange}
            data-qa={`field-visibility-box-switch-${fieldName}`}
          />
        </ForbiddenTooltip>
      </FieldBox>
    );
  }

  const isEmpty = defaultDays == null;

  const handleDaysChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    if (raw === '') {
      onDefaultDaysChange?.(fieldName, undefined);
      return;
    }
    const val = parseInt(raw, 10);
    if (!isNaN(val) && val > 0) {
      onDefaultDaysChange?.(fieldName, val);
    }
  };

  const handleQuickSelect = (days: number): void => {
    onDefaultDaysChange?.(fieldName, days);
  };

  return (
    <FieldBox dataQa={`field-visibility-box-${fieldName}`}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <span style={{ flexGrow: 1 }}>{label}</span>
          <ForbiddenTooltip
            visibleWhen={disabled}
            reason={FieldTranslationKeys.visibilitySwitchDisabled}
          >
            <Switch
              id={fieldName}
              checked={isChecked}
              disabled={disabled}
              color='primary'
              onChange={onChange}
              data-qa={`field-visibility-box-switch-${fieldName}`}
            />
          </ForbiddenTooltip>
        </Box>
        <Collapse in={isChecked} unmountOnExit>
          <Box sx={{ pb: 1, ml: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label={formatTranslation(intl, FieldTranslationKeys.configurationDefaultDaysLabel)}
                  type='number'
                  size='small'
                  value={defaultDays ?? ''}
                  onChange={handleDaysChange}
                  placeholder='—'
                  inputProps={{ min: 1, max: 999, step: 1 }}
                  helperText={
                    isEmpty
                      ? formatTranslation(intl, FieldTranslationKeys.configurationNoDeadlineHint)
                      : formatTranslation(intl, FieldTranslationKeys.configurationDeadlineHint , { days: defaultDays })
                  }
                  FormHelperTextProps={{
                    sx: { color: isEmpty ? 'text.disabled' : 'text.secondary', whiteSpace: 'nowrap' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' sx={{ visibility: isEmpty ? 'hidden' : 'visible' }}>
                        <Typography variant='caption' color='text.secondary'>
                          {formatTranslation(intl, FieldTranslationKeys.configurationDaysUnit , { days: defaultDays ?? 0 })}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 120 }}
                  data-qa={`field-configuration-default-days-${fieldName}`}
                />
              </Box>
              <Stack direction='row' spacing={0.5} sx={{ pt: 0.5, pr: 1, flexShrink: 0 }}>
                {QUICK_OPTIONS.map((d) => {
                  const isSelected = defaultDays === d;
                  return (
                    <Chip
                      key={d}
                      label={formatTranslation(intl, FieldTranslationKeys.configurationDaysChip , { days: d })}
                      size='small'
                      variant='outlined'
                      onClick={() => handleQuickSelect(d)}
                      sx={{
                        cursor: 'pointer',
                        ...(isSelected
                          ? {
                              backgroundColor: Color.primaryOrangePale,
                              borderColor: Color.border,
                              color: 'text.primary',
                              '&:hover': {
                                backgroundColor: Color.primaryOrangePale,
                                borderColor: Color.border,
                              },
                            }
                          : {
                              backgroundColor: 'background.paper',
                              borderColor: Color.neutralBorderGray,
                              color: 'text.secondary',
                            }),
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </FieldBox>
  );
}
